/**
 * useEntityDisplayName — resolve just an entity's display name from the store,
 * fetching only the name properties if it isn't cached yet.
 *
 * This is the display-name-only counterpart to {@link useCrmObjectSnapshot}.
 * Unlike a full-object fetch, this hook reads summary entries (the shape
 * {@link ensureEntityName} writes) and triggers a *name-scoped* fetch. Use it
 * where a component needs a name and nothing else — e.g. an association
 * highlight card resolving an owner/company name — so it doesn't over-fetch
 * the full record.
 *
 * The requested id is resolved through the merge-alias map before reading the
 * store, and the hook subscribes to the *global* store (not a per-id listener)
 * so that when the name batcher registers an alias for a merged entity — under
 * its canonical post-merge id — this hook re-renders and surfaces the name.
 * Subscribing only to the requested (pre-merge) id would leave merged entities
 * stuck loading forever, since their name lands under a different key.
 *
 * @param entityId - Entity UUID to resolve. Undefined → no fetch, no name.
 * @param orgId - Organization ID for the fetch. Undefined/<=0 → no fetch.
 * @returns `{ displayName, isLoading }`. `displayName` is undefined until
 *   resolved; `isLoading` is true while a name fetch is pending (no name yet
 *   and no recorded failure).
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  ensureEntityName,
  getNameFailureVersion,
  hasNameFetchFailed,
  subscribeToNameFetchFailures,
} from '@/lib/crm/object-cards/entityNameBatcher';
import {
  getStoredObject,
  resolveEntityId,
  subscribe as subscribeToStore,
} from '@/lib/crm/store';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';

const NOOP_SUBSCRIBE = (_cb: () => void) => () => {};

export interface EntityDisplayNameResult {
  /** Resolved display name, or undefined while loading / unresolved. */
  displayName: string | undefined;
  /** True while a name fetch is pending (no name yet, no recorded failure). */
  isLoading: boolean;
  /** True when the most recent name fetch for this entity failed. */
  hasFailed: boolean;
}

export function useEntityDisplayName(
  entityId: string | undefined,
  orgId: number | undefined
): EntityDisplayNameResult {
  // Subscribe to the GLOBAL store (not a per-id listener): the name batcher
  // may register a merge alias for `entityId` and write the name under the
  // canonical post-merge id, which only fires a global change. A per-id
  // listener on the requested id would never observe that. getSnapshot reads
  // through `resolveEntityId` and `getStoredObject` (summary entries included,
  // unlike subscribeToObject which hides incomplete entries).
  const subscribe = useCallback(
    (cb: () => void) => (entityId ? subscribeToStore(cb) : NOOP_SUBSCRIBE(cb)),
    [entityId]
  );
  const getSnapshot = useCallback(
    () => (entityId ? getStoredObject(resolveEntityId(entityId)) : undefined),
    [entityId]
  );
  const stored = useSyncExternalStore(subscribe, getSnapshot);

  // Re-render when the name-fetch failure set changes so loading can exit.
  useSyncExternalStore(subscribeToNameFetchFailures, getNameFailureVersion);

  const displayName = useMemo(() => {
    if (!stored?.data) {
      return undefined;
    }
    const name = getDisplayName(stored.data, '');
    return name === '' ? undefined : name;
  }, [stored]);

  const needsFetch = entityId ? displayName === undefined : false;

  useEffect(() => {
    if (entityId && orgId && orgId > 0 && needsFetch) {
      ensureEntityName(orgId, entityId);
    }
  }, [orgId, entityId, needsFetch]);

  const hasFailed = Boolean(entityId && hasNameFetchFailed(entityId));
  const isLoading = Boolean(
    orgId && orgId > 0 && entityId && displayName === undefined && !hasFailed
  );

  return { displayName, isLoading, hasFailed };
}
